import React, { useEffect, useState } from "react";
import { customerApi, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import PhoneInput from "@/components/forms/PhoneInput";

/**
 * Edit My Info — opened by tapping the "Hi, {name}" greeting in the
 * top-right of the Guest Portal. Edits Email, WhatsApp, DOB and
 * Gender. Iter-364 · Clean Removal: password change UI removed —
 * the portal is passwordless (OTP-only sign-in).
 *
 * Universal Data Sync (SYSTEM_RULES §3b): every save fires the
 * `cocoon:customer-updated` window event so mounted Reception views
 * (BrandCustomerDetail, BranchCalendar, MyRosterDay, BranchOrders)
 * repaint the guest's new contact details immediately without a
 * manual refresh. Backend `PUT /customers/me` also emits the WS
 * `customer_updated` broadcast for cross-tab sync.
 */
export default function EditMyInfoDialog({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState({
    email: "",
    whatsapp: "",
    birthday: "",
    gender: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      email: customer?.email || "",
      whatsapp: customer?.whatsapp || "",
      birthday: customer?.birthday || "",
      gender: customer?.gender || "",
    });
  }, [open, customer]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await customerApi.put("/customers/me", form);
      toast.success("Profile updated");
      // Universal Data Sync — broadcast so open Reception / Staff views
      // for this customer repaint immediately.
      try {
        window.dispatchEvent(
          new CustomEvent("cocoon:customer-updated", {
            detail: { customer_id: customer?.id },
          }),
        );
      } catch (_) { /* noop */ }
      if (typeof onSaved === "function") onSaved(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-my-info-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit my info</DialogTitle>
          <DialogDescription>
            Update your contact details and date of birth. Changes sync instantly to reception.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1"
              data-testid="edit-email-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-whatsapp">WhatsApp</Label>
            <PhoneInput
              value={form.whatsapp}
              onChange={(val) => setForm({ ...form, whatsapp: val })}
              placeholder="WhatsApp number"
              testId="edit-whatsapp-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-dob">Date of birth</Label>
            <Input
              id="edit-dob"
              type="date"
              value={form.birthday}
              onChange={(e) => setForm({ ...form, birthday: e.target.value })}
              className="mt-1"
              data-testid="edit-dob-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-gender">Gender</Label>
            <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger id="edit-gender" className="mt-1" data-testid="edit-gender-select">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="female" data-testid="edit-gender-female">Female</SelectItem>
                <SelectItem value="male" data-testid="edit-gender-male">Male</SelectItem>
                <SelectItem value="other" data-testid="edit-gender-other">Other</SelectItem>
                <SelectItem value="prefer_not_to_say" data-testid="edit-gender-private">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Iter-364 · Clean Removal. Change-password block deleted —
              the guest portal is passwordless. Guests sign in with a
              one-time code emailed to their address on every visit. */}
          <div className="pt-2 border-t border-border/60">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Sign-in</p>
            <p className="text-xs text-muted-foreground mt-2">
              Cocoon uses passwordless sign-in. We'll email you a one-time code from <span className="font-medium text-foreground">/portal/login</span> whenever you need to access your portal.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="edit-my-info-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="edit-my-info-save"
          >
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
